import { useState } from 'react';
import { TextInput } from 'carbon-components-react';
import CustomButton from '../../components/custom-button/custom-btn.component';
import './SBAForm.scss';
import {
  getItemById,
  getQueryString,
  useReducerSpread,
} from '../../utils/common.util';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { postMethod } from '../../api/common';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
  calculateTotal,
  calculateSBA,
  calculateTotalMarks,
  calculateFinalPercentage,
  SBAAbbreviationsNote,
} from './calculation-helpers';
import PageLayout from '../../components/PageLayout';
import { useClassAccessList } from '../../api/class.service';
import { StatsBar } from '../../components/PrintOutHead';
import { useQueryParams } from '../../utils/useQueryParams';
import CreateSBAModal from './CreateSBAModal';
import { PlusCircle } from 'react-feather';

const max = {
  classExercises: 10,
  homeworks: 10,
  cats: 10,
  project: 20,
};

export default function CreateSBA() {
  const history = useHistory();

  const {
    subjectId: urlSubjectId,
    classId: urlClassId,
    termId: urlTermId,
    academicYearId: urlAcademicYearId,
  } = useQueryParams('subjectId', 'classId', 'termId', 'academicYearId');

  // if (!urlSubjectId || !urlClassId || !urlTermId || !urlAcademicYearId) {
  //   history.push('/sba-view');
  // }

  const [formData, setFormData] = useReducerSpread({
    subjectId: urlSubjectId ?? '',
    classId: urlClassId ?? '',
    termId: urlTermId ?? '',
    academicYearId: urlAcademicYearId ?? '',
  });
  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    formData.academicYearId
      ? `academic-year/terms/?academicYearId=${formData.academicYearId}`
      : null,
  );

  const { classAccessList: classes } = useClassAccessList();

  const { data: subjects } = useSWR(
    formData.classId
      ? `academic/subject/list/?classId=${formData.classId}`
      : null,
  );
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    formData.classId
      ? `student/list?classId=${formData.classId}&status=Enrolled`
      : null,
  );

  const [scores, setScores] = useState({});

  // Helper: update score for a student
  const handleScoreChange = (studentId, type, idx, value) => {
    if (value === '' || (Number(value) >= 0 && Number(value) <= max[type])) {
      setScores((prev) => {
        const studentKey = `student_${studentId}`;
        const prevStudent = prev[studentKey] || {
          classExercises: Array(10).fill(''),
          homeworks: Array(10).fill(''),
          cats: Array(3).fill(''),
          project: '',
        };
        let updated = { ...prevStudent };
        if (type === 'project') {
          updated.project = value;
        } else {
          const arr = [...updated[type]];
          arr[idx] = value;
          updated[type] = arr;
        }
        return { ...prev, [studentKey]: updated };
      });
    }
  };

  const { trigger, error, isMutating } = useSWRMutation(
    `academic/grading/create-school-based-assessment`,
    (url, { arg }) => postMethod(url, arg),
    {
      onSuccess: () => {
        history.push(
          // `/sba-view?classId=${formData.classId}&subjectId=${formData.subjectId}&termId=${formData.termId}`,
          `/sba-view?${getQueryString(formData)}`,
        );
      },
      throwOnError: false,
    },
  );
  const handleSubmit = (e) => {
    e.preventDefault();

    const studentScores = students.map((student) => {
      const studentKey = `student_${student.id}`;
      const studentData = scores[studentKey] || {}; // Use empty object as default

      return {
        studentId: student.id,
        classExercises: (studentData.classExercises || [])
          .filter((s) => s !== '' && s !== null)
          .map(Number),
        homeworks: (studentData.homeworks || [])
          .filter((s) => s !== '' && s !== null)
          .map(Number),
        classTests: (studentData.cats || [])
          .filter((s) => s !== '' && s !== null)
          .map(Number),
        projectWork: Number(studentData.project || 0),
      };
    });

    const payload = {
      termId: Number(formData.termId),
      subjectId: Number(formData.subjectId),
      studentScores,
    };

    trigger(payload);
  };

  const [isOpen, setIsOpen] = useState('');

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academics', to: '/academic' },
        { label: 'Create SBA', active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">Create SBA</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('create-sba')}
          >
            <PlusCircle size="14" strokeWidth="2px" />
            <span>Create New SBA</span>
          </button>
        </div>
      }
      isLoading={isStudentsLoading}
    >
      <>
        <StatsBar
          statsItems={[
            {
              label: 'Class',
              value: getItemById(classes, formData.classId)?.description,
            },
            {
              label: 'Subject',
              value: getItemById(subjects, formData.subjectId)?.description,
            },

            {
              label: 'Academic Year',
              value: getItemById(academicYears, formData.academicYearId)
                ?.description,
            },
            {
              label: 'Term',
              value: getItemById(terms, formData.termId)?.description,
            },
          ]}
        />
        <form
          className="mt-20 sba-form"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          {/* <div className="sba-form__header card-row-4">
            <Select
              labelText="Academic Year"
              onChange={(e) =>
                setFormData({ academicYearId: e.target.value, termId: '' })
              }
              value={formData.academicYearId}
            >
              <SelectItem value="" text="" />
              {academicYears?.map((year) => (
                <SelectItem
                  key={year.id}
                  value={year.id}
                  text={year.description}
                />
              ))}
            </Select>
            <Select
              labelText="Term"
              disabled={!formData.academicYearId}
              onChange={(e) => setFormData({ termId: e.target.value })}
              value={formData.termId}
            >
              <SelectItem value="" text="" />
              {terms?.map((term) => (
                <SelectItem
                  key={term.id}
                  value={term.id}
                  text={term.description}
                />
              ))}
            </Select>
            <Select
              labelText="Class"
              onChange={(e) =>
                setFormData({ classId: e.target.value, subjectId: '' })
              }
              value={formData.classId}
            >
              <SelectItem value="" text="" />
              {classes?.map((term) => (
                <SelectItem
                  key={term.id}
                  value={term.id}
                  text={term.description}
                />
              ))}
            </Select>
            <Select
              labelText="Subject"
              disabled={!formData.classId}
              onChange={(e) => setFormData({ subjectId: e.target.value })}
              value={formData.subjectId}
            >
              <SelectItem value="" text="" />
              {subjects?.map((term) => (
                <SelectItem
                  key={term.id}
                  value={term.id}
                  text={term.description}
                />
              ))}
            </Select>
          </div> */}
          {/* display error message */}
          {error && (
            <p className="text-14 mt-10 text-danger">{error.message}</p>
          )}
          <div style={{ overflowX: 'auto' }}>
            {students?.length > 0 && (
              <table className="bx--data-table sba-form__table sba-form__table--sticky-student">
                <thead>
                  <tr>
                    <th rowSpan="2" className="sba-form__sticky-col">
                      Student
                    </th>
                    <th colSpan="10">CLASS EXERCISE</th>
                    <th rowSpan="2">
                      Total C.E. <br /> (10)
                    </th>
                    <th colSpan="10">HOMEWORK</th>
                    <th rowSpan="2">
                      Total H/W <br /> (10)
                    </th>
                    <th rowSpan="2">
                      Total <br /> (C.E. + H/W) <br /> (10)
                    </th>
                    <th colSpan="3">CATS</th>
                    <th rowSpan="2">
                      Project <br /> (20)
                    </th>
                    <th rowSpan="2">Total (60)</th>
                    <th rowSpan="2">Final % (50)</th>
                  </tr>
                  <tr>
                    {[...Array(10)].map((_, i) => (
                      <th key={`ce-head-${i}`}> {i + 1}</th>
                    ))}
                    {[...Array(10)].map((_, i) => (
                      <th key={`hw-head-${i}`}> {i + 1}</th>
                    ))}
                    {[...Array(3)].map((_, i) => (
                      <th key={`cat-head-${i}`}> {i + 1}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students?.map((student) => {
                    const studentKey = `student_${student.id}`;
                    const s = scores[studentKey] || {
                      classExercises: Array(10).fill(''),
                      homeworks: Array(10).fill(''),
                      cats: Array(3).fill(''),
                      project: '',
                    };
                    const ce10 = calculateTotal(s.classExercises, 10);
                    const hw10 = calculateTotal(s.homeworks, 10);
                    const sba10 = calculateSBA(ce10, hw10);
                    const total60 = calculateTotalMarks(
                      sba10,
                      s.cats,
                      s.project,
                    );
                    const final50 = calculateFinalPercentage(total60);
                    return (
                      <tr key={student.id}>
                        <td className="sba-form__student-name sba-form__sticky-col">
                          {student.fullName}
                        </td>
                        {s.classExercises.map((v, i) => (
                          <td key={i}>
                            <TextInput
                              className="sba-form__input"
                              type="number"
                              min="0"
                              max="10"
                              value={v}
                              onChange={(e) =>
                                handleScoreChange(
                                  student.id,
                                  'classExercises',
                                  i,
                                  e.target.value,
                                )
                              }
                              hideLabel
                              id={`ce-${student.id}-${i}`}
                              labelText=""
                              autoComplete="off"
                            />
                          </td>
                        ))}
                        <td>
                          <span className="sba-form__readonly">{ce10}</span>
                        </td>
                        {s.homeworks.map((v, i) => (
                          <td key={i}>
                            <TextInput
                              className="sba-form__input"
                              type="number"
                              min="0"
                              max="10"
                              value={v}
                              onChange={(e) =>
                                handleScoreChange(
                                  student.id,
                                  'homeworks',
                                  i,
                                  e.target.value,
                                )
                              }
                              hideLabel
                              id={`hw-${student.id}-${i}`}
                              labelText=""
                              autoComplete="off"
                            />
                          </td>
                        ))}
                        <td>
                          <span className="sba-form__readonly">{hw10}</span>
                        </td>
                        <td>
                          <span className="sba-form__readonly">{sba10}</span>
                        </td>
                        {s.cats.map((v, i) => (
                          <td key={i}>
                            <TextInput
                              className="sba-form__input"
                              type="number"
                              min="0"
                              max="10"
                              value={v}
                              onChange={(e) =>
                                handleScoreChange(
                                  student.id,
                                  'cats',
                                  i,
                                  e.target.value,
                                )
                              }
                              hideLabel
                              id={`cat-${student.id}-${i}`}
                              labelText=""
                              autoComplete="off"
                            />
                          </td>
                        ))}
                        <td>
                          <TextInput
                            className="sba-form__input"
                            type="number"
                            min="0"
                            max="20"
                            value={s.project}
                            onChange={(e) =>
                              handleScoreChange(
                                student.id,
                                'project',
                                0,
                                e.target.value,
                              )
                            }
                            hideLabel
                            id={`project-${student.id}`}
                            labelText=""
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <span className="sba-form__readonly">{total60}</span>
                        </td>
                        <td>
                          <span className="sba-form__readonly">{final50}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {students?.length === 0 && (
              <p className="text-14 mt-30 text-grey">
                No students in this class.
              </p>
            )}
          </div>

          {students?.length > 0 && (
            <div
              style={{
                marginTop: 24,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              <SBAAbbreviationsNote />

              <CustomButton
                type="submit"
                classes="primary font-bold"
                disabled={!formData.termId || !formData.subjectId}
                style={{ minWidth: 160 }}
                isLoading={isMutating}
              >
                SUBMIT
              </CustomButton>
            </div>
          )}
        </form>
      </>

      <CreateSBAModal
        isOpen={isOpen === 'create-sba'}
        closeModal={() => setIsOpen('')}
        setExternalFormData={setFormData}
      />
    </PageLayout>
  );
}
